import { hardNavigation } from "../../utils/hard-navigation.util";
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, Subscription } from "rxjs";
import { Store } from "@ngrx/store";
import { ClipboardService } from "ngx-clipboard";

import { SignOutMessageService } from "src/app/components/sign-out-message/sign-out-message.service";
import { AuthService } from "src/app/core/core-services/services/auth.service";
import { trackService } from "src/app/core/core-services/services/track.service";
import { CrossAppAuthService } from "src/app/core/core-services/services/cross-app-auth.service";
import { CTA_UPGRADE_PLAN_URL, SUBSCRIPTION_TAB } from "src/app/vengreso/settings/settings.config";
import { NotifyService } from "../menu/not.service";
import { MenuService } from "../menu/menu.service";
import { ExtensionService } from "../core-services/services/extension.service";
import { NavigationService } from "../core-services/services/navigation.service";
import { NotificationService } from "../core-services/services/notification.service";
import { SharedDataService } from "../core-services/services/shared-data.service";
import { selectPlanMenuDisplayName, selectCanUpgrade, selectIsFreemium } from "../store/plan/plan.selectors";
import { MasqueradeService } from "../core-services/services/masquerade.service";
import { normalizeAvatarUrl } from "src/app/utils/avatar.util";
import { userHasNotificationCenterAccess } from "src/app/core/models/user.model";

declare global {
  interface Window {
    heap: any;
  }
}

@Component({
    selector: "app-header",
    templateUrl: "./header.component.html",
    styleUrls: ["./header.component.scss"],
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  userDetail: any;

  /**
   * fix(M9): the header avatar `<img>` had no `(error)` handler, so a 404/403
   * avatar left a broken-image glyph for the rest of the session. Flip to the
   * same `person` icon used when the user has no avatar at all.
   */
  avatarFailed = false;

  /** Route-independent avatar URL — see `normalizeAvatarUrl`. */
  get avatarSrc(): string {
    return normalizeAvatarUrl(this.userDetail?.avatar);
  }

  browser_type;
  show = false;
  notifierSubscription: Subscription = this.notifyService.subjectNotifier.subscribe((notified) => {
    this.userDetail = this.authService.userDetails.user_details;
    this.cdr.detectChanges();
  });
  showAdminSection: boolean;
  showCMCSection: boolean;

  // NgRx observables for plan data
  planId$: Observable<string>;
  canUpgrade$: Observable<boolean>;
  isFreemium$: Observable<boolean>;
  canUpgrade: boolean = true;

  constructor(
    private readonly router: Router,
    public authService: AuthService,
    public trackService: trackService,
    public notifyService: NotifyService,
    private readonly cdr: ChangeDetectorRef,
    public signOutMessageService: SignOutMessageService,
    public menuService: MenuService,
    private readonly sharedData: SharedDataService,
    private readonly notification: NotificationService,
    private readonly crossAppAuth: CrossAppAuthService,
    private readonly store: Store,
    private readonly masqueradeService: MasqueradeService,
    private readonly extensionService: ExtensionService,
    private readonly navigationService: NavigationService,
    private readonly clipboardService: ClipboardService
  ) {
    this.browser_type = this.sharedData.getAttribute("browser_type");
    if (this.browser_type == "firefox" || this.browser_type == "opera" || this.browser_type == "safari") {
      this.show = false;
    } else {
      this.show = true;
    }

    this.userDetail = this.authService.userDetails.user_details;
    this.showAdminSection = this.authService.showAdminSection();
    this.showCMCSection = this.authService.showVengresoAdminSection();
  }

  ngOnInit(): void {
    // Initialize NgRx observables for plan data
    this.planId$ = this.store.select(selectPlanMenuDisplayName);
    this.canUpgrade$ = this.store.select(selectCanUpgrade);
    this.isFreemium$ = this.store.select(selectIsFreemium);

    // Subscribe to canUpgrade$ for click handler
    this.canUpgrade$.subscribe((value) => {
      this.canUpgrade = value;
    });
  }

  handleUpgradePlan(): void {
    if (this.canUpgrade) {
      hardNavigation.navigate(CTA_UPGRADE_PLAN_URL);
    }
  }

  endMasqueradeSession(): void {
    this.masqueradeService.endMasquerade().subscribe({
      next: (cmcUrl) => {
        this.notification.toast("Masquerade ended - redirecting to admin", "success");
        hardNavigation.navigate(cmcUrl);
      },
      error: (err) => {
        console.error("Failed to end masquerade:", err);
        this.notification.toast("Failed to end masquerade session", "danger");
      },
    });
  }

  get invitedToCompany(): boolean {
    return this.userDetail.invited_to_company && this.userDetail.invited_to_company_by_admin;
  }

  get isCompanyUser() {
    const user = this.authService.userDetails.user_details;

    return user?.company_id && user?.status !== "Invited";
  }

  /**
   * Issue #416's mandatory product gate (updated for #559): the notification
   * bell (and everything it starts — REST fetch, socket connection, polling
   * — all live inside `AppNotificationService`/`NotificationBellComponent`)
   * renders only for users entitled via the per-user feature map's
   * `in_app_notifications` key. See `userHasNotificationCenterAccess`'s
   * JSDoc — this is the single source of truth for the gate, not a locally
   * re-read flag.
   */
  get showNotificationCenter(): boolean {
    return userHasNotificationCenterAccess(this.userDetail);
  }

  acceptInvitation(): void {
    let user_id = this.userDetail.id;
    let company_id = this.userDetail.company_id;
    this.authService.acceptCompanyInvitation(company_id, user_id).subscribe({
      next: () => {
        this.notification.toast("Invitation Accepted!", "Ok", 4000);
        setTimeout(() => {
          hardNavigation.reload();
        }, 1000);
      },
      error: (error: any) => {
        console.error("acceptCompanyInvitation failed", error);
        const message =
          error?.error?.message ||
          error?.error?.error ||
          error?.message ||
          "Failed to accept invitation. Please try again.";
        this.notification.toast(message, "danger", 6000);
      },
    });
  }

  rejectInvitation(): void {
    let user_id = this.userDetail.id;
    let company_id = this.userDetail.company_id;
    this.authService.rejectCompanyInvitation(company_id, user_id).subscribe({
      next: () => {
        this.notification.toast("Invitation Rejected!", "Ok", 4000);
        setTimeout(() => {
          hardNavigation.reload();
        }, 1000);
      },
      error: (error: any) => {
        console.error("rejectCompanyInvitation failed", error);
        const message =
          error?.error?.message ||
          error?.error?.error ||
          error?.message ||
          "Failed to reject invitation. Please try again.";
        this.notification.toast(message, "danger", 6000);
      },
    });
  }

  copyToClipboard(email: string): void {
    if (this.clipboardService.copyFromContent(email)) {
      this.notification.toast("Email copied to clipboard!", "Ok", 5000);
    }
  }

  onMenuOpened(): void {
    // Plan data is now managed by NgRx store, no need to refresh
  }

  logOut() {
    this.signOutMessageService.show();
  }

  goToProfile() {
    this.router.navigate(["/profile"]);
  }

  goToSettings(): void {
    this.router.navigate(["/settings"], {
      queryParams: { tab: SUBSCRIPTION_TAB },
    });
  }

  goToAdmin(): void {
    const adminMenu = this.menuService.getAll().find((menu) => menu.label == "Administration");
    const url = adminMenu.sublink_two;

    // For external links (cross-app navigation), open in same window
    if (adminMenu.isAdminExternal) {
      hardNavigation.navigate(url);
    } else {
      window.open(url, "_blank");
    }
  }

  goToCMCAdmin(): void {
    const adminMenu = this.menuService.getAll().find((menu) => menu.label == "Administration");
    const url = adminMenu.sublink_three;

    // For external links (cross-app navigation), open in same window
    if (adminMenu.isCmcExternal) {
      hardNavigation.navigate(url);
    } else {
      window.open(url, "_blank");
    }
  }

  navigateTo(site: any) {
    this.navigationService.navigateToUrl(site);
  }

  navigateToExtension() {
    this.trackService.btnClickUpdatetrack("clicked_download_extension");
    this.extensionService.openExtensionStorePage();
  }

  ngOnDestroy(): void {
    this.notifierSubscription.unsubscribe();
  }
}
